import {
    collection,
    doc,
    onSnapshot,
    query,
    where,
    orderBy,
} from "firebase/firestore";
import { firestore } from '_utils/firebase-app';
import { getAuth } from 'firebase/auth';

const COLLECTION = 'incidentReports';

export const createIncident = async (data) => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();

    const res = await fetch('/api/v1/timeline/incidents', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Firebase-UID': token } : {}),
        },
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Failed to create incident: ${res.status}`);
    }

    const json = await res.json();
    return json.id;
};

export const subscribeToIncidents = (iata, callback) => {
    const q = query(
        collection(firestore, COLLECTION),
        where('iata', '==', iata),
        orderBy('date', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
        const incidents = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(incidents);
    });
};

export const subscribeToIncident = (incidentId, callback) => {
    const docRef = doc(firestore, COLLECTION, incidentId);
    return onSnapshot(docRef, (d) => {
        if (d.exists()) callback({ id: d.id, ...d.data() });
    });
};

const getAuthHeader = async () => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    return token ? { 'Firebase-UID': token } : {};
};

export const updateIncidentStatus = async (incidentId, status) => {
    const headers = await getAuthHeader();
    const res = await fetch(`/api/v1/timeline/incidents/${incidentId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ status }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Failed to update status: ${res.status}`);
    }
};

export const updateIncident = async (incidentId, fields) => {
    const headers = await getAuthHeader();
    const res = await fetch(`/api/v1/timeline/incidents/${incidentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(fields),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Failed to update incident: ${res.status}`);
    }
};

// recipientIds is optional — omit (or pass an empty array) to return the report
// to every assigned agent; pass a subset to return it to just those agents.
export const returnIncident = async (incidentId, comment, recipientIds) => {
    const headers = await getAuthHeader();
    const res = await fetch(`/api/v1/timeline/incidents/${incidentId}/return`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
            comment,
            ...(recipientIds?.length ? { recipientIds } : {}),
        }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Failed to return incident: ${res.status}`);
    }
};

export const deleteIncident = async (incidentId) => {
    const headers = await getAuthHeader();
    const res = await fetch(`/api/v1/timeline/incidents/${incidentId}`, {
        method: 'DELETE',
        headers,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Failed to delete incident: ${res.status}`);
    }
};
